import { useEffect, useState, useCallback } from "react";
import { shouldDisableServiceWorkersInCurrentContext } from "@/lib/appVersion";

/**
 * Detects when a new service worker is waiting and exposes a controlled
 * apply-update action. Auto-reloads as soon as the new SW takes control.
 *
 * Strategy:
 * 1. On mount: if there's already a `waiting` SW (installed in a previous
 *    session), surface it immediately.
 * 2. Listen for `updatefound` → when new worker reaches `installed` state
 *    AND there's an existing controller (= update, not first install),
 *    surface it.
 * 3. `applyUpdate()` posts SKIP_WAITING to the waiting worker.
 * 4. `controllerchange` fires when the new SW takes over → reload page
 *    immediately. No query-param tricks needed.
 */
export function useServiceWorkerUpdate() {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [waitingWorker, setWaitingWorker] = useState<ServiceWorker | null>(null);

  const applyUpdate = useCallback(() => {
    if (!waitingWorker) {
      // Fallback: no waiting worker tracked but user hit "update" → just reload
      window.location.reload();
      return;
    }
    waitingWorker.postMessage({ type: "SKIP_WAITING" });
    // controllerchange listener (below) will handle the reload
  }, [waitingWorker]);

  useEffect(() => {
    if (!("serviceWorker" in navigator) || shouldDisableServiceWorkersInCurrentContext()) {
      return;
    }

    let mounted = true;
    let reloadOnControllerChange = false;

    const surfaceWaiting = (worker: ServiceWorker | null) => {
      if (!mounted || !worker) return;
      setWaitingWorker(worker);
      setUpdateAvailable(true);
    };

    const onControllerChange = () => {
      // Some browsers fire this on initial registration too — only reload
      // if we explicitly triggered an update flow.
      if (reloadOnControllerChange) {
        reloadOnControllerChange = false;
        window.location.reload();
      }
    };
    navigator.serviceWorker.addEventListener("controllerchange", onControllerChange);

    // Wrap state updates so reload happens when SKIP_WAITING was sent
    const trackedApplyUpdate = (worker: ServiceWorker) => {
      reloadOnControllerChange = true;
      worker.postMessage({ type: "SKIP_WAITING" });
    };

    navigator.serviceWorker.getRegistration().then((reg) => {
      if (!reg || !mounted) return;

      // 1) Already waiting from a previous load — surface immediately
      if (reg.waiting && navigator.serviceWorker.controller) {
        surfaceWaiting(reg.waiting);
      }

      // 2) New worker installing right now
      reg.addEventListener("updatefound", () => {
        const newWorker = reg.installing;
        if (!newWorker) return;
        newWorker.addEventListener("statechange", () => {
          if (newWorker.state === "installed" && navigator.serviceWorker.controller) {
            surfaceWaiting(newWorker);
          }
        });
      });

      // Force a network check for new SW versions
      reg.update().catch(() => {});

      // Re-check on visibility change — catches updates published while user away
      const onVisibility = () => {
        if (document.visibilityState === "visible") {
          reg.update().catch(() => {});
        }
      };
      document.addEventListener("visibilitychange", onVisibility);

      // Cleanup for visibility listener happens via outer cleanup
      (reg as any).__vt_visHandler = onVisibility;
    });

    // Expose tracked apply for the caller
    (window as any).__vt_applyUpdate = (worker: ServiceWorker) => trackedApplyUpdate(worker);

    return () => {
      mounted = false;
      navigator.serviceWorker.removeEventListener("controllerchange", onControllerChange);
      navigator.serviceWorker.getRegistration().then((reg) => {
        if (reg && (reg as any).__vt_visHandler) {
          document.removeEventListener("visibilitychange", (reg as any).__vt_visHandler);
        }
      });
    };
  }, []);

  // Wrap applyUpdate so it always sets reload flag via the tracked path
  const applyUpdateTracked = useCallback(() => {
    if (waitingWorker && (window as any).__vt_applyUpdate) {
      (window as any).__vt_applyUpdate(waitingWorker);
    } else {
      applyUpdate();
    }
  }, [waitingWorker, applyUpdate]);

  return { updateAvailable, applyUpdate: applyUpdateTracked };
}
